/* eslint-disable react/jsx-indent-props */
import React from 'react';
import PropTypes from 'prop-types';
import { Subheader, Paper, FlatButton, FontIcon, CircularProgress, TextField, SelectField, MenuItem } from 'material-ui';
import { FormattedMessage } from 'react-intl';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { flowRight, isEmpty, get } from 'lodash';

import PanelMessage from 'components/panels/PanelMessage';
import { normalizeNumber, normalizeNumberRange0to100 } from 'utils/normalize';
import convert from 'utils/dateTools';
import { shouldEdit, shouldCreate } from 'utils/valuesByKey';
import { setStatus, update, createTaxCard } from 'actions/EmployeeTaxCards';
import {
  TAX_CARD_FIXED as FIXED,
  TAX_CARD_SAVING as SAVING,
  TAX_CARD_EDIT as EDIT,
  TAX_CARD_CREATE as CREATE,
} from 'constants/statuses';
import TField from './TField';
import { validateTaxCard } from './helpers';
import styles from './styles/TaxCard';

const EmployeeTaxCard = (props) => {
  const {
    card, handleSubmit, invalid, status, valuesByKey, lastUsedOptions, cardType, createCard,
  } = props;

  const cardName = cardType === 1 ? 'EmployeeMasterTaxCard' : 'EmployeeMasterTaxCardSpouse';

  const startCreate = () => props.setStatus({ id: cardType, value: CREATE });

  if (isEmpty(card) && status !== CREATE) {
    if (cardType === 2 && !shouldCreate(valuesByKey, cardName)) return null;

    return (
      <PanelMessage
        message={<FormattedMessage id={`taxCard.none.${cardName}`} />}
        iconName="info"
        type="info"
        action={shouldCreate(valuesByKey, cardName) ? startCreate : null}
        actionLabel={<FormattedMessage id="taxCard.create" />}
      />
    );
  }

  const onEdit = () => props.setStatus({ id: cardType, value: EDIT });
  const onSave = data => props.update(data);
  const onCreate = data => createCard({ ...data, TaxCardType: cardType });

  const fieldEnabled = key => (status === CREATE && shouldCreate(valuesByKey, cardName))
    || shouldEdit(valuesByKey, `EmployeeMasterTaxCard.${key}`);

  const buttonEnabled = () => (status === CREATE && shouldCreate(valuesByKey, cardName))
    || shouldEdit(valuesByKey, 'EmployeeMasterTaxCard');

  const isEditing = status === EDIT || status === CREATE;

  const currentMonth = new Date().getMonth();

  const allowLastUsedEdit = get(card, 'DateLastUsed')
    ? new Date(card.DateLastUsed).getTime() < new Date(new Date().getFullYear(), 0, 1).getTime()
    : true;

  const editIcon = status === SAVING
    ? <CircularProgress style={{ lineHeight: '14px' }} size={14} thickness={2} />
    : <FontIcon style={{ fontSize: 16 }} className="material-icons">edit</FontIcon>;

  const btnIcon = isEditing
    ? <FontIcon style={{ fontSize: 16 }} className="material-icons">save</FontIcon>
    : editIcon;

  const chooseMethod = () => {
    if (status === EDIT) return handleSubmit(onSave);
    if (status === CREATE) return handleSubmit(onCreate);
    return onEdit;
  };

  return (
    <Paper>
      <Subheader>
        <FormattedMessage id={cardType === 1 ? 'taxCard.title.employee' : 'taxCard.title.spouse'} />
      </Subheader>
      <form style={styles.container}>
        <Field
          name="Prosent"
          component={TField}
          label={<FormattedMessage id="taxCard.percent" />}
          customStyle={{ width: 120 }}
          disabled={status === FIXED}
          maxLength="3"
          normalize={normalizeNumberRange0to100}
        />
        <Field
          name="Unused"
          component={TField}
          label={<FormattedMessage id="taxCard.unused" />}
          customStyle={{ width: 120 }}
          disabled={status === FIXED || !fieldEnabled('Unused')}
          normalize={normalizeNumber}
        />
        {allowLastUsedEdit
          ? <Field
            name="DateLastUsed"
            component={field => (
              <SelectField
                {...field.input}
                style={styles.inputCon}
                selectedMenuItemStyle={{ cursor: 'default' }}
                underlineDisabledStyle={styles.inputDisabled}
                onChange={(e, i, v) => field.input.onChange(v, i, v)}
                onBlur={() => null}
                onFocus={() => null}
                floatingLabelText={<FormattedMessage id="taxCard.lastUsed" />}
                floatingLabelFixed
                disabled={status === FIXED || !fieldEnabled('DateLastUsed')}
              >
                {lastUsedOptions.map(x => <MenuItem {...x} disabled={x.key < currentMonth} />)}
              </SelectField>
            )}
          />
          : <TextField
            floatingLabelText={<FormattedMessage id="taxCard.lastUsed" />}
            style={styles.inputCon}
            underlineDisabledStyle={styles.inputDisabled}
            floatingLabelFixed
            disabled
            inputStyle={styles.input}
            value={convert(get(card, 'DateLastUsed'))}
          />
        }
        {buttonEnabled() && (
          <FlatButton
            style={styles.editButton}
            labelStyle={styles.editButtonLabel}
            label={<FormattedMessage id={isEditing ? 'taxCard.save' : 'taxCard.edit'} />}
            onClick={chooseMethod()}
            primary={isEditing}
            disabled={isEditing ? invalid : status === SAVING}
            icon={btnIcon}
          />
        )}
      </form>
    </Paper>
  );
};

EmployeeTaxCard.propTypes = {
  cardType: PropTypes.number.isRequired,
  card: PropTypes.object,
  valuesByKey: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired, // needed for RootForm
  setStatus: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
  status: PropTypes.oneOf([FIXED, SAVING, EDIT, CREATE]),
  invalid: PropTypes.bool.isRequired,
  lastUsedOptions: PropTypes.array.isRequired,
  createCard: PropTypes.func.isRequired,
};

EmployeeTaxCard.defaultProps = {
  status: FIXED,
};

const mapStateToProps = ({ EmployeeTaxCardsReducer, WebValuesReducer }, props) => {
  const card = props.cardType === 1
    ? EmployeeTaxCardsReducer.employeeTaxCard
    : EmployeeTaxCardsReducer.spouseTaxCard;
  return {
    card,
    status: EmployeeTaxCardsReducer[`status-${props.cardType}`],
    valuesByKey: WebValuesReducer.valuesByKey,
    lastUsedOptions: EmployeeTaxCardsReducer.lastUsedOptions,
    // required fields for RootForm:
    form: `EMTaxCard-${props.cardType}`,
    initialValues: card,
    validate: values => validateTaxCard(values, EmployeeTaxCardsReducer.taxDiscount),
  };
};

const mapDispatchToProps = dispatch => ({
  setStatus: status => dispatch(setStatus(status)),
  update: data => dispatch(update(data)),
  createCard: data => dispatch(createTaxCard(data)),
});

export default flowRight(connect(
  mapStateToProps,
  mapDispatchToProps,
), reduxForm({}))(EmployeeTaxCard);



// WEBPACK FOOTER //
// ./src/containers/pay/EmployeeTaxCard.js